import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Dismissible} from "@khan/component-dismissible";
import {useQuery} from "@khan/data-gql";

import type {khanmigoToolsLocalesForTeacherToolsBanner_localesForLanguagePicker as LocaleData} from "./graphql/__graphql-types__/khanmigoToolsLocalesForTeacherToolsBanner.ts";
import {TEACHER_TOOLS_LOCALE_BANNER_QUERY} from "./graphql/language-availability-query.ts";
import {ToolPersona} from "./types.ts";

interface Props {
    kaLocale: string;
    persona: ToolPersona;
}

const VALID_KA_LOCALES = new Set<string>(["en", "pt", "es", "hi", "tr", "vi"]);

export const LanguageAvailabilityBanner = (
    props: Props,
): React.ReactElement => {
    const {kaLocale, persona} = props;
    const [localesResult] = useQuery(TEACHER_TOOLS_LOCALE_BANNER_QUERY);

    const renderLocaleBannerMessage: () => React.ReactNode = () => {
        const localesForLanguagePicker =
            localesResult.status === "success"
                ? localesResult.data.localesForLanguagePicker
                : null;

        const match: LocaleData | null | undefined =
            localesForLanguagePicker?.find(
                (locale) => locale?.kaLocale === kaLocale,
            );

        // TO DO (bcdirito):
        // Take a look at lingui docs to make sure this is the best way to dynamically
        // render translated text.
        return (
            <>
                {match && (
                    <Trans>
                        {persona === ToolPersona.Parent
                            ? `Khanmigo for Parents is not yet fully available in ${match.localName}.`
                            : `Khanmigo for Teachers is not yet fully available in ${match.localName}.`}
                    </Trans>
                )}
                {!match &&
                    (persona === ToolPersona.Parent
                        ? t`Khanmigo for Parents is not yet fully available in your selected language.`
                        : t`Khanmigo for Teachers is not yet fully available in your selected language.`)}
                &nbsp;
                <Trans>
                    If you are seeing a suboptimal experience, please set your
                    primary language to English in your{" "}
                    {/* TODO: Update with new URL for classroom settings page once it's available. */}
                    <Link href="/settings/account">account settings</Link>.
                </Trans>
            </>
        );
    };

    // If the user's kaLocale is English, Spanish, Brazilian Portuguese,
    // Hindi, or Turkish do not render the banner.
    if (VALID_KA_LOCALES.has(kaLocale)) {
        return <></>;
    }

    if (localesResult.status === "loading") {
        return <CircularSpinner />;
    }

    return (
        <Dismissible
            dismissKey="khanmigo-locale-support-banner"
            expiresDays="never"
        >
            {(dismiss) => (
                <View style={styles.bannerContainer}>
                    <Banner
                        testId="language-availability-banner"
                        text={renderLocaleBannerMessage()}
                        kind="info"
                        layout="floating"
                        onDismiss={dismiss}
                        dismissAriaLabel={t`Dismiss banner`}
                    />
                </View>
            )}
        </Dismissible>
    );
};

const styles = StyleSheet.create({
    bannerContainer: {
        marginBlockEnd: spacing.xLarge_32,
    },
});
